import { DefaultError, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { RefObject } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

import HabitatLiveStreamPlayer from '@/components/HabitatLiveStreamPlayer';
import { useHabitatLiveStreamPlayerContext } from '@/components/HabitatLiveStreamPlayer/contexts';
import CameraSelector from '@/components/HabitatLiveStreamPlayer/Controls/CameraSelector';
// import LiveStreamIframe from '@/components/LiveStreamIframe';
import LiveStreamPlayer from '@/components/LiveStreamPlayer';
import { useLiveStreamPlayerStateContext } from '@/components/LiveStreamPlayer/contexts';
import LiveStreamPlayerControlsDesktop from '@/components/LiveStreamPlayer/LiveStreamPlayerControls/Desktop';
import { FullscreenControlButton } from '@/components/VideoControls/buttons';
import { Habitat, HabitatAPIResponse } from '@/redux/slices/habitats/types';

interface HomeLiveStreamPlayerProps {
  habitatSlug: string;
  zooSlug: string;
  miniMode?: boolean;
  preferLiveStreamMutedRef: RefObject<boolean>;
}

const LiveStreamPlayerAddon = ({
  miniMode,
  preferLiveStreamMutedRef,
}: Pick<HomeLiveStreamPlayerProps, 'miniMode' | 'preferLiveStreamMutedRef'>) => {
  const { isPlaying, isVideoLoaded, requestChangePlayingState, requestChangeMuteState } = useLiveStreamPlayerStateContext();

  useEffect(() => {
    if (miniMode) {
      requestChangeMuteState(true);
    } else {
      requestChangeMuteState(!!preferLiveStreamMutedRef.current);
    }
  }, [miniMode, requestChangeMuteState, preferLiveStreamMutedRef]);

  if (!isVideoLoaded) {
    return null;
  }

  return isPlaying ? (
    <div
      className={clsx(
        'absolute flex items-center gap-1 rounded bg-olive-4',
        miniMode ? 'right-2 top-2 px-1.5 py-1' : 'right-5 top-3 p-1.5',
      )}
    >
      <span className="size-1 rounded-full bg-system-red" />
      <span className={clsx('font-bold leading-none', miniMode ? 'text-[8px]' : 'text-[10px]')}>LIVE</span>
    </div>
  ) : (
    <div className="absolute inset-0 flex items-center justify-center bg-slate-900 text-white">
      <button type="button" className="preflight preflight-button" onClick={() => requestChangePlayingState(true)}>
        Play
      </button>
    </div>
  );
};

const Player = ({
  containerRef,
  miniMode,
  preferLiveStreamMutedRef,
}: Pick<HomeLiveStreamPlayerProps, 'miniMode' | 'preferLiveStreamMutedRef'> & {
  containerRef: RefObject<HTMLDivElement>;
}) => {
  const { camera } = useHabitatLiveStreamPlayerContext();

  return camera ? (
    <LiveStreamPlayer.Root streamId={camera.streamKey}>
      <LiveStreamPlayer.Player className="group-data-[mini-mode=true]/home-player:rounded" />
      <LiveStreamPlayer.Logger />
      <LiveStreamPlayer.StatsLogger />
      <LiveStreamPlayer.StatsTrigger />

      {/* Controls */}
      <LiveStreamPlayerControlsDesktop.Container className="translate-y-full transition-transform group-hover/home-video-section:translate-y-0 group-data-[mini-mode=true]/home-player:hidden">
        <LiveStreamPlayerControlsDesktop.ButtonsGroup>
          <LiveStreamPlayerControlsDesktop.PlayPauseButton />
          <LiveStreamPlayerControlsDesktop.SoundControlButtonGroup
            onMutedChange={(newValue) => {
              // eslint-disable-next-line no-param-reassign
              preferLiveStreamMutedRef.current = newValue;
            }}
          />
          <FullscreenControlButton fullscreenElementRef={containerRef} />
          <CameraSelector />
        </LiveStreamPlayerControlsDesktop.ButtonsGroup>
      </LiveStreamPlayerControlsDesktop.Container>

      <LiveStreamPlayerAddon preferLiveStreamMutedRef={preferLiveStreamMutedRef} miniMode={miniMode} />
    </LiveStreamPlayer.Root>
  ) : null;
};

const HomeLiveStreamPlayer = ({ habitatSlug, zooSlug, miniMode, preferLiveStreamMutedRef }: HomeLiveStreamPlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: habitat } = useQuery<HabitatAPIResponse, DefaultError, Habitat>({
    queryKey: [`/zoos/${zooSlug}/habitats/${habitatSlug}`],
    select: ({ habitat }) => habitat,
    enabled: false, // !!(habitatSlug && zooSlug),
  });

  return (
    <div ref={containerRef} className="relative h-full overflow-hidden">
      {habitat && (
        <HabitatLiveStreamPlayer.Root habitat={habitat}>
          <Player preferLiveStreamMutedRef={preferLiveStreamMutedRef} containerRef={containerRef} miniMode={miniMode} />
        </HabitatLiveStreamPlayer.Root>
      )}
    </div>
  );
};

export default HomeLiveStreamPlayer;
